/*
 * ─────────────────────────────────────────────
 * SHIFTBRIDGE CANONICAL SHIFT SERVICE
 * ─────────────────────────────────────────────
 *
 * This is the public mutation boundary for shifts.
 *
 * Screens should gradually import shift actions
 * from this service instead of importing
 * firestoreShifts.js directly.
 *
 * firestoreShifts.js remains the persistence
 * implementation during the migration.
 *
 * UI must not own shift business logic.
 */
import {
  createShift as createShiftRecord,

  assignWorkerToShift as assignWorkerToShiftRecord,

  requestPoolShift as requestPoolShiftRecord,

  declineShift as declinePoolRequestRecord,

  approvePoolRequest as approvePoolRequestRecord,

    acceptPlannerAssignment as acceptPlannerAssignmentRecord,

  declinePlannerAssignment as declinePlannerAssignmentRecord,

  acceptShiftOffer as acceptShiftOfferRecord,

  declineShiftOffer as declineShiftOfferRecord,

  reassignWorkerOnShift as reassignWorkerOnShiftRecord,

  updateShiftTime as updateShiftTimeRecord,

confirmShiftTime as confirmShiftTimeRecord,

removeShift as removeShiftRecord,

  closeShiftUnfilled as closeShiftUnfilledRecord,

  setShiftActivated as activateShiftRecord,

  setShiftTracking as startShiftTrackingRecord,

  setShiftArrived as markShiftArrivedRecord,

  setShiftCompleted as completeShiftRecord,

  setShiftNoShow as markShiftNoShowRecord,
} from "../../firestoreShifts";

/* ─────────────────────────────
   CREATE
───────────────────────────── */

export async function createShift(
  shiftData = {}
) {
  return createShiftRecord(
    shiftData
  );
}

/* ─────────────────────────────
   ASSIGNMENT
───────────────────────────── */

export async function assignWorkerToShift(
  shiftId,
  workerUid
) {
  return assignWorkerToShiftRecord(
    shiftId,
    workerUid
  );
}

export async function reassignWorkerOnShift(
  shiftId,
  workerUid,
  reason = "manual"
) {
  return reassignWorkerOnShiftRecord(
    shiftId,
    workerUid,
    reason
  );
}

export async function unassignWorkerFromShift(
  shiftId,
  reason = "manual_unassign"
) {
  return reassignWorkerOnShiftRecord(
    shiftId,
    null,
    reason
  );
}

/* ─────────────────────────────
   POOL REQUEST
───────────────────────────── */

export async function requestPoolShift(
  shiftId,
  workerUid
) {
  return requestPoolShiftRecord(
    shiftId,
    workerUid
  );
}

export async function declinePoolRequest(
  shiftId,
  workerUid
) {
  return declinePoolRequestRecord(
    shiftId,
    workerUid
  );
}

export async function approvePoolRequest(
  shiftId,
  workerUid,
  reason = "pool_approved"
) {
  return approvePoolRequestRecord(
    shiftId,
    workerUid,
    reason
  );
}

/* ─────────────────────────────
   WORKER ACCEPTANCE
───────────────────────────── */

export async function acceptPlannerAssignment(
  shiftId,
  workerUid
) {
  return acceptPlannerAssignmentRecord(
    shiftId,
    workerUid
  );
}

export async function declinePlannerAssignment(
  shiftId,
  workerUid,
  reason = "Unable to work this shift"
) {
  return declinePlannerAssignmentRecord(
    shiftId,
    workerUid,
    reason
  );
}

export async function acceptDirectOffer(
  shiftId,
  workerUid
) {
  return acceptShiftOfferRecord(
    shiftId,
    workerUid
  );
}

export async function declineDirectOffer(
  shiftId,
  workerUid,
  fallbackToPool = true
) {
  return declineShiftOfferRecord(
    shiftId,
    workerUid,
    fallbackToPool
  );
}

/* ─────────────────────────────
   SHIFT TIME
───────────────────────────── */

export async function updateShiftTime(
  shiftId,
  startTime,
  endTime
) {
  return updateShiftTimeRecord(
    shiftId,
    startTime,
    endTime
  );
}

export async function confirmShiftTime({
  shiftId,
  startTime,
  endTime,
  actorUid,
  timeSlot = "Custom",
  assignedWorkerUid = "",
}) {
  return confirmShiftTimeRecord({
    shiftId,
    startTime,
    endTime,
    actorUid,
    timeSlot,
    assignedWorkerUid,
  });
}

/* ─────────────────────────────
   REMOVE / CANCEL
───────────────────────────── */

export async function removeShift(
  shiftId,
  actorUid,
  reason = "planner_remove"
) {
  return removeShiftRecord(
    shiftId,
    actorUid,
    reason
  );
}

export async function closeShiftUnfilled(
  shiftId,
  actorUid,
  reason = "closed_unfilled"
) {
  return closeShiftUnfilledRecord(
    shiftId,
    actorUid,
    reason
  );
}

/* ─────────────────────────────
   ACTIVE LIFECYCLE
───────────────────────────── */

export async function activateShift(
  shiftId,
  workerUid,
  activationWindowHours = 2
) {
  return activateShiftRecord(
    shiftId,
    workerUid,
    activationWindowHours
  );
}

export async function startShiftTracking(
  shiftId,
  workerUid
) {
  return startShiftTrackingRecord(
    shiftId,
    workerUid
  );
}

export async function markShiftArrived(
  shiftId,
  workerUid,
  arrivalMeta = {}
) {
  return markShiftArrivedRecord(
    shiftId,
    workerUid,
    arrivalMeta
  );
}

export async function completeShift(
  shiftId,
  workerUid,
  actorUid = null
) {
  return completeShiftRecord(
    shiftId,
    workerUid,
    actorUid
  );
}

/* ─────────────────────────────
   EXCEPTIONS
───────────────────────────── */

export async function markShiftNoShow(
  shiftId,
  plannerUid,
  reason = "Marked no-show"
) {
  return markShiftNoShowRecord(
    shiftId,
    plannerUid,
    reason
  );
}
export { withdrawPoolRequest, cancelShift } from "../../firestoreShifts";
